'use client';

import * as React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import type { PluggableList } from 'unified';
import { cn } from '@/lib/utils';
import { createCodeComponent } from './code-handler';

const remarkPlugins: PluggableList = [remarkGfm, remarkMath];
const rehypePlugins: PluggableList = [[rehypeKatex, { output: 'mathml' }]];
const markdownOverflowWrapClasses =
  'min-w-0 max-w-full break-words [overflow-wrap:anywhere]';

// =============================================================================
// Variant-aware color mapping
// =============================================================================

import type { MarkdownVariant } from './types';

export type { MarkdownVariant } from './types';

const variantColors = {
  heading: { default: 'text-foreground', inverted: 'text-primary-foreground' },
  body: {
    default: 'text-secondary-foreground',
    inverted: 'text-primary-foreground/90',
  },
  link: { default: 'text-primary', inverted: 'text-primary-foreground' },
  muted: {
    default: 'text-muted-foreground',
    inverted: 'text-primary-foreground/70',
  },
  faint: {
    default: 'text-secondary-foreground',
    inverted: 'text-primary-foreground/50',
  },
  border: {
    default: 'border-border',
    inverted: 'border-primary-foreground/25',
  },
  bgMuted: { default: 'bg-muted', inverted: 'bg-background/20' },
} as const;

type ColorToken = keyof typeof variantColors;

function vc(variant: MarkdownVariant, token: ColorToken): string {
  return variantColors[token][variant];
}

// =============================================================================
// Create variant-aware component overrides for react-markdown
// =============================================================================

function createMarkdownComponents(
  variant: MarkdownVariant,
): Partial<Components> {
  return {
    h1: ({ className, children, ...props }) => (
      <h1
        className={cn(
          'text-display-xs font-bold mt-6 mb-4',
          markdownOverflowWrapClasses,
          vc(variant, 'heading'),
          className,
        )}
        {...props}
      >
        {children}
      </h1>
    ),
    h2: ({ className, children, ...props }) => (
      <h2
        className={cn(
          'text-xl font-semibold mt-5 mb-3',
          markdownOverflowWrapClasses,
          vc(variant, 'heading'),
          className,
        )}
        {...props}
      >
        {children}
      </h2>
    ),
    h3: ({ className, children, ...props }) => (
      <h3
        className={cn(
          'text-lg font-semibold mt-4 mb-2',
          markdownOverflowWrapClasses,
          vc(variant, 'heading'),
          className,
        )}
        {...props}
      >
        {children}
      </h3>
    ),
    h4: ({ className, children, ...props }) => (
      <h4
        className={cn(
          'text-base font-semibold mt-3 mb-2',
          markdownOverflowWrapClasses,
          vc(variant, 'heading'),
          className,
        )}
        {...props}
      >
        {children}
      </h4>
    ),
    p: ({ className, children, ...props }) => (
      <p
        className={cn(
          'text-base leading-relaxed mb-4',
          markdownOverflowWrapClasses,
          vc(variant, 'body'),
          className,
        )}
        {...props}
      >
        {children}
      </p>
    ),
    ul: ({ className, children, ...props }) => (
      <ul
        className={cn(
          'list-disc list-inside mb-4 space-y-1',
          markdownOverflowWrapClasses,
          vc(variant, 'body'),
          className,
        )}
        {...props}
      >
        {children}
      </ul>
    ),
    ol: ({ className, children, ...props }) => (
      <ol
        className={cn(
          'list-decimal list-inside mb-4 space-y-1',
          markdownOverflowWrapClasses,
          vc(variant, 'body'),
          className,
        )}
        {...props}
      >
        {children}
      </ol>
    ),
    li: ({ className, children, ...props }) => (
      <li
        className={cn(
          'leading-relaxed',
          markdownOverflowWrapClasses,
          className,
        )}
        {...props}
      >
        {children}
      </li>
    ),
    a: ({ className, children, href, ...props }) => (
      <a
        href={href}
        className={cn(
          'underline underline-offset-2 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm',
          markdownOverflowWrapClasses,
          vc(variant, 'link'),
          variant === 'default' && 'hover:text-primary',
          variant === 'inverted' && 'hover:text-primary-foreground',
          className,
        )}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    ),
    blockquote: ({ className, children, ...props }) => (
      <blockquote
        className={cn(
          'border-s-4 ps-4 py-2 my-4 italic',
          markdownOverflowWrapClasses,
          vc(variant, 'muted'),
          vc(variant, 'border'),
          className,
        )}
        {...props}
      >
        {children}
      </blockquote>
    ),
    code: createCodeComponent(variant),
    pre: ({ children }) => <>{children}</>,
    hr: ({ className, ...props }) => (
      <hr className={cn('my-6', vc(variant, 'border'), className)} {...props} />
    ),
    table: ({ className, children, ...props }) => (
      <section
        className="not-prose overflow-x-auto my-4 [overflow-wrap:normal] [&_a]:break-normal [&_a]:[overflow-wrap:normal] [&_code]:break-normal [&_code]:[overflow-wrap:normal] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        aria-label="Data table"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: Focusable scroll region satisfies axe scrollable-region-focusable.
        tabIndex={0}
      >
        <table
          className={cn(
            'min-w-full border-collapse border [overflow-wrap:normal]',
            vc(variant, 'border'),
            variant === 'inverted' && '!bg-transparent',
            className,
          )}
          {...props}
        >
          {children}
        </table>
      </section>
    ),
    thead: ({ className, children, ...props }) => (
      <thead
        className={cn(
          variant === 'default' ? vc(variant, 'bgMuted') : '!bg-transparent',
          className,
        )}
        {...props}
      >
        {children}
      </thead>
    ),
    tbody: ({ className, children, ...props }) => (
      <tbody
        className={cn(
          variant === 'default'
            ? 'divide-y divide-border'
            : 'divide-y divide-primary-foreground/20',
          className,
        )}
        {...props}
      >
        {children}
      </tbody>
    ),
    tr: ({ children, ...props }) => <tr {...props}>{children}</tr>,
    th: ({ className, children, ...props }) => (
      <th
        className={cn(
          'px-4 py-2 text-start text-sm font-semibold border',
          '[overflow-wrap:normal]',
          vc(variant, 'body'),
          vc(variant, 'border'),
          variant === 'inverted' && '!bg-transparent',
          className,
        )}
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ className, children, ...props }) => (
      <td
        className={cn(
          'px-4 py-2 text-sm border',
          '[overflow-wrap:normal]',
          vc(variant, 'body'),
          vc(variant, 'border'),
          variant === 'inverted' && '!bg-transparent',
          className,
        )}
        {...props}
      >
        {children}
      </td>
    ),
    strong: ({ className, children, ...props }) => (
      <strong
        className={cn(
          'font-semibold',
          markdownOverflowWrapClasses,
          vc(variant, 'heading'),
          className,
        )}
        {...props}
      >
        {children}
      </strong>
    ),
    em: ({ className, children, ...props }) => (
      <em className={cn(markdownOverflowWrapClasses, className)} {...props}>
        {children}
      </em>
    ),
    del: ({ className, children, ...props }) => (
      <del
        className={cn(
          'line-through',
          markdownOverflowWrapClasses,
          vc(variant, 'faint'),
          className,
        )}
        {...props}
      >
        {children}
      </del>
    ),
    img: ({ className, alt, src, ...props }) => (
      <img
        src={src}
        alt={alt}
        className={cn('max-w-full h-auto rounded-lg my-4', className)}
        loading="lazy"
        {...props}
      />
    ),
  };
}

// =============================================================================
// Component cache (at most 2 entries: 'default' and 'inverted')
// =============================================================================

const componentCache = new Map<MarkdownVariant, Partial<Components>>();

function getComponents(variant: MarkdownVariant): Partial<Components> {
  let cached = componentCache.get(variant);
  if (!cached) {
    cached = createMarkdownComponents(variant);
    componentCache.set(variant, cached);
  }
  return cached;
}

// =============================================================================
// MarkdownRenderer
// =============================================================================

export interface MarkdownRendererProps {
  /** Markdown content to render */
  children: string;
  /** Additional class name */
  className?: string;
  /** Color variant — 'inverted' for white text on colored backgrounds */
  variant?: MarkdownVariant;
}

const MarkdownRenderer = React.memo(function MarkdownRenderer({
  children,
  className,
  variant = 'default',
}: MarkdownRendererProps) {
  const components = getComponents(variant);

  return (
    <div
      data-slot="ai-markdown"
      className={cn(
        'prose prose-gray max-w-none',
        markdownOverflowWrapClasses,
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export { MarkdownRenderer };
